import React, { useEffect, useMemo, useRef, useState } from "react";

import { MapPin, Search, Star } from "lucide-react";

import { useCart } from "../Context/CartContext";
import { useGlobalContext } from "../Context/GlobalContext";
import CartDrawer from "./CartDrawer";

import { useNavigate, useSearchParams } from "react-router-dom";

import NoImg from "../Images/Item_Image_Placeholder.webp";

import { useRestaurant } from "../Services/useRestaurant";
import { useCatalogueStatus } from "../Services/useCatalogueStatus";
import ChaafoLogo from "../Images/Restaurant_Logo_Placeholder.webp";

// =========================================================
// NORMALIZE SEARCH
// =========================================================

const normalizeSearch = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

// =========================================================
// GET VALUE FROM STORAGE
// =========================================================

const getStoredValue = (...keys) => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    for (const key of keys) {
      const sessionValue = sessionStorage.getItem(key);

      if (sessionValue) {
        return sessionValue;
      }

      const localValue = localStorage.getItem(key);

      if (localValue) {
        return localValue;
      }
    }
  } catch (error) {
    console.error("Storage read error:", error);
  }

  return "";
};

const getRestaurantServingType = (restaurant) => {
  return String(
    restaurant?.servingType ||
      restaurant?.serving_type ||
      restaurant?.raw?.servingType ||
      restaurant?.raw?.serving_type ||
      "",
  )
    .trim()
    .toUpperCase();
};

// =========================================================
// VEG / NON VEG ICON
// =========================================================

const FoodTypeIcon = ({ type }) => {
  if (type === "V") {
    return (
      <div
        title="Veg"
        className="
          flex
          h-5
          w-5
          items-center
          justify-center
          bg-white/95
          shadow-sm
        "
      >
        <span
          className="
            flex
            h-4
            w-4
            items-center
            justify-center
            border
            border-[#16a34a]
            bg-white
          "
        >
          <span className="h-2 w-2 rounded-full bg-[#16a34a]" />
        </span>
      </div>
    );
  }

  if (type === "N") {
    return (
      <div
        title="Non Veg"
        className="
          flex
          h-5
          w-5
          items-center
          justify-center
          bg-white/95
          shadow-sm
        "
      >
        <span
          className="
            flex
            h-4
            w-4
            items-center
            justify-center
            border
            border-[#b91c1c]
            bg-white
          "
        >
          <span className="h-2 w-2 rounded-full bg-[#b91c1c]" />
        </span>
      </div>
    );
  }

  return null;
};

// =========================================================
// SEARCH RESULTS
// =========================================================

const SearchResults = () => {
  const navigate = useNavigate();

  const searchRequestRef = useRef("");

  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get("q")?.trim() || "";

  const { getRestaurants } = useRestaurant();
const { getCatalogueStatus } = useCatalogueStatus();
  const [searchInput, setSearchInput] = useState(query);

  const [restaurants, setRestaurants] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  // =========================================================
  // KEEP INPUT IN SYNC WITH URL
  // =========================================================

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  // =========================================================
  // CALL restaurantGetViaCategoryV1
  // =========================================================

  useEffect(() => {
    if (!query) {
      setRestaurants([]);

      return;
    }

    let cancelled = false;

    const loadSearchResults = async () => {
      const latitude = getStoredValue(
        "selectedDeliveryLatitude",
        "latitude",
        "customerLatitude",
        "in_latitude",
      );

      const longitude = getStoredValue(
        "selectedDeliveryLongitude",
        "longitude",
        "customerLongitude",
        "in_longitude",
      );

      if (!latitude || !longitude) {
        setError("Location is required to search nearby restaurants.");

        setRestaurants([]);

        return;
      }

      const requestKey = `${query}-${latitude}-${longitude}`;

      if (searchRequestRef.current === requestKey) {
        return;
      }

      searchRequestRef.current = requestKey;

      try {
        setLoading(true);

        setError("");

        // =====================================================
        // GET RESTAURANTS
        // =====================================================

        const result = await getRestaurants({
          categoryIds: [],

          customerLatitude: latitude,

          customerLongitude: longitude,

          limit: 100,

          offset: 0,

          range: 20,

          servingType: "",

          bestOf: query,

          append: false,
        });

        if (cancelled) {
          return;
        }

        if (!result?.success) {
          setRestaurants([]);

          setError(result?.message || "Unable to search.");

          return;
        }

        const restaurantList = Array.isArray(result?.data) ? result.data : [];

        setRestaurants(restaurantList);
      } catch (searchError) {
        if (cancelled) {
          return;
        }

        console.error("Search page error:", searchError);

        setRestaurants([]);

        setError("Unable to load search results.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSearchResults();

    return () => {
      cancelled = true;
    };
  }, [query, getRestaurants]);

  // =========================================================
  // MATCHING RESTAURANTS
  // =========================================================

  const matchingRestaurants = useMemo(() => {
    return restaurants;
  }, [restaurants]);

  // =========================================================
  // SEARCH SUBMIT
  // =========================================================

  const handleSubmit = (event) => {
    event.preventDefault();

    const value = searchInput.trim();

    if (!value) {
      return;
    }

    setSearchParams({
      q: value,
    });
  };

  // =========================================================
  // OPEN RESTAURANT
  // =========================================================

 const openRestaurant = async (data) => {
   const restaurantUserId = String(
     data?.userId ||
       data?.user_id ||
       data?.restaurantCode ||
       data?.restaurantId ||
       "",
   ).trim();

   if (!restaurantUserId) {
     return;
   }

   const searchBestFor = String(query || "").trim();

   // =========================================================
   // GET LATEST RESTAURANT OPEN / CLOSED STATUS
   // =========================================================

   const statusResult = await getCatalogueStatus({
     userid: restaurantUserId,
   });

   const catalogueStatus = statusResult?.success
     ? String(statusResult?.catalogueStatus || "")
         .trim()
         .toLowerCase()
     : "";

   const isRestaurantOpen = catalogueStatus === "show";

   console.log("RESTAURANT STATUS:", {
     userid: restaurantUserId,
     catalogueStatus,
     isRestaurantOpen,
   });

   // =========================================================
   // OPEN RESTAURANT
   // =========================================================

   navigate(`/restaurant-details/${restaurantUserId}`, {
     state: {
       restaurant: {
         ...(data?.restaurant || data),

         catalogueStatus,
       },

       selectedItemName: searchBestFor,

       bestFor: searchBestFor,

       // IMPORTANT:
       // RestaurantMenu will use this value
       catalogueStatus,
     },
   });
 };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* ================================================= */}
        {/* SEARCH BAR */}
        {/* ================================================= */}

        <form
          onSubmit={handleSubmit}
          className="
            mx-auto
            mb-8
            flex
            max-w-3xl
            items-center
            gap-3
            rounded-full
            border
            border-gray-200
            bg-white
            px-5
            shadow-sm
          "
        >
          <Search
            size={21}
            className="
              shrink-0
              text-gray-400
            "
          />

          <input
            type="text"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
            }}
            placeholder="Search restaurants or dishes..."
            className="
              h-14
              min-w-0
              flex-1
              bg-transparent
              text-sm
              text-gray-800
              outline-none
            "
          />

          <button
            type="submit"
            className="
              rounded-full
              bg-[#11182A]
              px-5
              py-2
              text-sm
              font-semibold
              text-white
              transition

              hover:bg-[#252D40]
            "
          >
            Search
          </button>
        </form>

        {/* ================================================= */}
        {/* TITLE */}
        {/* ================================================= */}

        <div className="mb-6">
          <h1
            className="
              text-2xl
              font-bold
              text-gray-900

              sm:text-3xl
            "
          >
            Search results
          </h1>

          {query && (
            <p
              className="
                mt-1
                text-sm
                text-gray-500
              "
            >
              Results for{" "}
              <span
                className="
                  rounded-md
                  bg-gray-200
                  px-2
                  py-0.5
                  font-bold
                  text-gray-900
                "
              >
                {query}
              </span>
            </p>
          )}
        </div>

        {/* ================================================= */}
        {/* LOADING */}
        {/* ================================================= */}

        {loading && (
          <div
            className="
              flex
              min-h-[300px]
              items-center
              justify-center
            "
          >
            <div
              className="
                flex
                flex-col
                items-center
                gap-3
              "
            >
              <div
                className="
                  h-9
                  w-9
                  animate-spin
                  rounded-full
                  border-4
                  border-gray-200
                  border-t-[#11182A]
                "
              />

              <p
                className="
                  text-sm
                  text-gray-500
                "
              >
                Searching...
              </p>
            </div>
          </div>
        )}

        {/* ================================================= */}
        {/* ERROR */}
        {/* ================================================= */}

        {!loading && error && (
          <div
            className="
                rounded-2xl
                border
                border-red-100
                bg-red-50
                p-5
                text-sm
                text-red-600
              "
          >
            {error}
          </div>
        )}

        {/* ================================================= */}
        {/* RESULTS */}
        {/* ================================================= */}

        {!loading && !error && (
          <>
            {/* =========================================== */}
            {/* RESTAURANTS */}
            {/* =========================================== */}

            {matchingRestaurants.length > 0 && (
              <section className="mt-8">
                {/* HEADER */}

                <div className="mb-5">
                  <h2
                    className="
                        text-2xl
                        font-bold
                        text-[#111827]
                      "
                  >
                    Restaurants
                  </h2>

                  <p
                    className="
                        mt-1
                        text-sm
                        text-[#6B7280]
                      "
                  >
                    {matchingRestaurants.length}{" "}
                    {matchingRestaurants.length === 1
                      ? "Restaurant"
                      : "Restaurants"}{" "}
                    found
                  </p>
                </div>

                {/* ======================================= */}
                {/* RESTAURANT GRID */}
                {/* ======================================= */}

                <div
                  className="
                      grid
                      grid-cols-1
                      gap-5

                      sm:grid-cols-2

                      xl:grid-cols-4
                    "
                >
                  {matchingRestaurants.map((restaurant, index) => {
                    const restaurantId = String(
                      restaurant?.id ||
                        restaurant?.userId ||
                        restaurant?.user_id ||
                        index,
                    );

                    const image = Array.isArray(restaurant?.image)
                      ? restaurant.image[0]
                      : restaurant?.image;

                   const servingType = getRestaurantServingType(restaurant);

                    return (
                      <button
                        key={restaurantId}
                        type="button"
                        onClick={() => openRestaurant(restaurant)}
                        className="
                              overflow-hidden

                              rounded-[22px]

                              border
                              border-[#E5E7EB]

                              bg-white

                              text-left

                              shadow-sm

                              transition
                              duration-200

                              hover:-translate-y-0.5
                              hover:shadow-md
                            "
                      >
                        {/* ================================= */}
                        {/* IMAGE */}
                        {/* ================================= */}
                        <div className="relative h-[215px] w-full overflow-hidden bg-[#F8FAFC]">
                          <img
                            src={image || ChaafoLogo}
                            alt={restaurant?.name || "Restaurant"}
                            className="h-full w-full object-cover"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = ChaafoLogo;
                            }}
                          />

                          {/* VEG / NON VEG ICON - TOP LEFT */}

                          {(servingType === "V" || servingType === "N") && (
                            <div className="absolute left-2 top-2 z-10">
                              <FoodTypeIcon type={servingType} />
                            </div>
                          )}

                          {/* OFFER - BOTTOM LEFT */}

                          {restaurant?.offer && (
                            <div
                              className="
        absolute
        bottom-2
        left-2
        z-20

        rounded-full
        bg-[#16a34a]

        px-2.5
        py-1

        text-[10px]
        font-semibold
        text-white

        shadow-md

        sm:bottom-3
        sm:left-3
        sm:px-3
        sm:text-[11px]
      "
                            >
                              {restaurant.offer}
                            </div>
                          )}

                          {/* RATING - TOP RIGHT */}

                          {Number(restaurant?.rating) > 0 && (
                            <div className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-full bg-[#16a34a] px-2 py-1 text-[10px] font-semibold text-white shadow-md sm:right-3 sm:top-3 sm:px-2.5 sm:text-[12px]">
                              <Star
                                size={12}
                                strokeWidth={0}
                                fill="currentColor"
                              />

                              <span>
                                {Number(restaurant.rating).toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* ================================= */}
                        {/* CARD CONTENT */}
                        {/* ================================= */}

                        <div className="p-4">
                          {/* RESTAURANT NAME */}

                          <h3
                            className="
                                  truncate
                                  text-[15px]
                                  font-bold
                                  text-[#111827]
                                "
                          >
                            {restaurant?.name || "Restaurant"}
                          </h3>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* =========================================== */}
            {/* NOTHING FOUND */}
            {/* =========================================== */}

            {matchingRestaurants.length === 0 && (
              <div
                className="
                    flex
                    min-h-[300px]
                    flex-col
                    items-center
                    justify-center

                    rounded-2xl

                    bg-white

                    text-center

                    shadow-sm
                  "
              >
                <Search size={40} className="text-gray-300" />

                <h2
                  className="
                      mt-4
                      font-semibold
                      text-gray-900
                    "
                >
                  No results found
                </h2>

                <p
                  className="
                      mt-1
                      text-sm
                      text-gray-500
                    "
                >
                  We couldn't find anything matching "{query}".
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
